import { ChangeEvent, ReactElement, ReactNode, useEffect, useState } from "react";
import { IDelayQMOS, IProductVariable, ISample, IState, IStrandProduction } from "../../../types";
import { Tabs, Tab } from "@mui/material";
import GeneralPanel from "./dialogPanels/generalPanel";
import ProductDetail from "./dialogPanels/productDetail";
import { useFetch } from "../../../system/useFetch";
import MillOrderChart from "./millOrderChart";
import { IMillProductDetail, IStageHistory } from "../types";
import ProductDetailChart, { IProductChartConfig, IPoint } from "./dialogPanels/productDetailChart";
import { IRadarChartConfig, IPoint as CommonPoint, IAxisLinear } from "../../../components/charts/types";
import { formatDateTimeWithSeconds } from "../../../system/utils";
import RadarChart from "../../../components/charts/radarChart";
import { getPalette } from "../../../configuration/configuration";
import { useSelector } from "react-redux";
import DelayPanel from "./dialogPanels/delayPanel";
import SamplesByMachine from "./dialogPanels/samplesByMachine";

interface IHeatInformationProps {
  productionItem: IStrandProduction;
  height?: number;
}

interface ITabPanelProps {
  children?: ReactNode;
  dir?: string;
  index: any;
  value: any;
}

function TabPanel(props: ITabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tab-panel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other} >
      {value === index && children}
    </div>
  );
}

const getDatetime  = (stage: IStageHistory | null, field: keyof IStageHistory, defaultValue: number ): number => {

  return stage && stage[field] ? stage[field] as number : defaultValue;
};

const ProductionInformation = ({ productionItem, height }: IHeatInformationProps): ReactElement => {
  const darkMode = useSelector((state: IState) => state.darkMode);
  const { data: response } = useFetch<[IMillProductDetail[], IProductVariable[], ISample[]]>(
    [
      { module: "mill", query: "getMillProductionDetailByID", payload: { productionMachineID: productionItem.productionMachineID } },
      { module: "mill", query: "getMillProductVariablesByOrderID", payload: { productionMachineID: productionItem.productionMachineID } },
      {
        module: "production",
        query: "getSamplesByProductionMachineList",
        payload: {
          productionMachineIDs: [
            productionItem.eafProductionMachineID ?? 0,
            productionItem.lrfProductionMachineID ?? 0,
            productionItem.casterProductionMachineID ?? 0 ] } }
    ]);

  const [value, setValue] = useState(0);
  const [selectedItem, setSelectedItem] = useState<IMillProductDetail | null>(null);
  const [selectedDelay, setSelectedDelay] = useState<IDelayQMOS | null>(null);
  const [chartConfig, setChartConfig] = useState<IProductChartConfig | null>(null);
  const [radarConfig, setRadarConfig] = useState<IRadarChartConfig | null>(null);


  useEffect(() => {
    if (response) {

      const items = response[0] as IMillProductDetail[];

      if (items.length === 0){
        return;
      }

      const points: IPoint[] = [];
      let stage627 = items[0].stages ? items[0].stages["CHS #627"] ?? null : null;

      let minX = getDatetime(stage627, "beginDatetime", productionItem.beginDatetime);
      let maxX = getDatetime(stage627, "endDatetime", productionItem.endDatetime ?? 0);
      let minY = items[0].weight ?  items[0].weight : Number.MAX_VALUE;
      let maxY = items[0].weight ? items[0].weight : 0;


      items.forEach(item => {
        stage627 = items[0].stages ? items[0].stages["CHS #627"] ?? null : null;
        if (stage627 && stage627.beginDatetime &&  stage627.endDatetime && item.weight) {

          const weight = item.weight;
          minX = Math.min(minX, stage627.beginDatetime);
          maxX = Math.max(maxX, stage627.endDatetime);
          minY = Math.min(minY, weight);
          maxY = Math.max(maxY, weight);


          points.push({
            title: `${item.billet ?? "---"}`,
            beginDatetime: stage627.beginDatetime,
            endDatetime: stage627.endDatetime,
            value: weight,
            data: item
          });
        }
      });

      const xAxis: IAxisLinear = {
        name: "Time",
        minimum: minX,
        maximum: maxX,
        ticksCount: 4,
        formatTick: (n) => formatDateTimeWithSeconds(n as number)
      };

      minY = minY === Number.MAX_VALUE ? 0 : minY;
      const factor = (maxY - minY) * .2;

      const yAxis: IAxisLinear = {
        name: "lb",
        minimum: minY - factor,
        maximum: maxY + factor,
        ticksCount: 4,
        formatTick: (n) => (n as number).toFixed(0)
      };

      const config: IProductChartConfig = {
        height: 300,
        series: {
          name: "Items",
          points,
          color: "#90A4AE",
          unit: "lb"
        },
        xAxis,
        yAxis
      };

      const radarCfg: IRadarChartConfig = {
        width: 300,
        height: 300,
        series: [{
          name: "Items",
          xAxisName: "Billets",
          yAxisName: "lb",
          value: items.filter(f => f.billet && f.weight).map(p => ( { x: `${p.billet}`, y: p.weight } )) as CommonPoint[],
          color: getPalette().list[5],
          unit: "lb"
        }],
        yAxis,
        xAxis: {
          name: "Billets",
          categories: items.filter(f => f.billet && f.weight).map(p => `${p.billet ?? "-"}`)
        }
      };

      setChartConfig(config);
      setRadarConfig(radarCfg);
    }
  }, [response]);

  const handleChange = (_event: ChangeEvent<any>, newValue: number) => {
    setValue(newValue);
  };

  const offset = productionItem.heatData && productionItem.heatData.heatID ? 0 : 1;


  const gridStyle = {
    height,
    gridTemplateRows: "auto auto 1fr",
    gridTemplateColumns: "1fr",

  };

  return (
    <div style={gridStyle}>
      <GeneralPanel productionItem={productionItem} billets={response ? response[0].length : 0}/>

      <div style={{ width: "100%", height: 120, marginTop: "1em", marginBottom: "1em" }}>
        <MillOrderChart productionData={productionItem} hideAxisTitle={true}
          products={response ? response[0] : null} setSelectedItem={setSelectedItem} selectedItem={selectedItem}
          selectedDelay={selectedDelay} setSelectedDelay={setSelectedDelay} />
      </div>

      <div>
        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          centered={true}
          style={{ background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)" }}>
          {productionItem.heatData && productionItem.heatData.heatID && <Tab label="Heat Samples"/>}
          <Tab label="Detail"/>
          {/*<Tab label="Samples"/>*/}
          <Tab label="Delays"/>
          <Tab label="Weight"/>
        </Tabs>
        {productionItem.heatData && productionItem.heatData.heatID &&
        <TabPanel value={value} index={0}>
          <SamplesByMachine samples={response ? response[2] ?? [] : []} steelCode={productionItem.heatData?.steel.code} />
        </TabPanel>
        }
        <TabPanel value={value} index={1 - offset}>
          <ProductDetail products={response ? response[0] : null} setSelectedItem={setSelectedItem} selectedItem={selectedItem}/>
        </TabPanel>
        <TabPanel value={value} index={2 - offset}>
          <DelayPanel delays={productionItem.delays as IDelayQMOS[]} selected={selectedDelay} setSelected={setSelectedDelay} />
        </TabPanel>
        <TabPanel value={value} index={3 - offset}>
          <div className="display-grid" style={{ gridTemplateColumns: "1fr auto", height: 300 }}>
            <div>
              {chartConfig && <ProductDetailChart chartConfig={chartConfig} selectedItem={selectedItem}  />}
            </div>
            {radarConfig && <RadarChart radarConfig={radarConfig} selectedItem={selectedItem ? selectedItem.billet : null}/>}
          </div>
        </TabPanel>
      </div>
    </div>
  );
};

export default ProductionInformation;
